import { useState, useEffect, useContext } from 'react';
import { Table, Spin, message, Button, Modal, Form, Input, InputNumber, Select } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import axios from 'axios';
import { LoginContext } from '../../../context/loginContext';

const { Option } = Select;

const debCreLabel = (v) => (Number(v) === 2 ? 'Crédito' : 'Débito');

const CTiposComprobantesAdministrador = () => {
    const [tiposComprobantes, setTiposComprobantes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();
    const { user } = useContext(LoginContext);

    useEffect(() => {
        fetchTiposComprobantes();
    }, []);

    const fetchTiposComprobantes = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/tipos-comprobantes`;
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const response = await axios.get(url, config);
            setTiposComprobantes(response.data);
        } catch (error) {
            console.error('Error obteniendo los tipos de comprobantes:', error);
            message.error('Error al cargar los tipos de comprobantes');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({ tic_debcre: 1 });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditing(record);
        form.setFieldsValue({
            codigo_afip: record.tic_codigo,
            tic_nombre: record.tic_nombre,
            tic_debcre: record.tic_debcre,
        });
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };

            if (editing) {
                const url = `${process.env.REACT_APP_API_URL}/tipos-comprobantes/${editing.tic_id}`;
                await axios.put(
                    url,
                    { tic_nombre: values.tic_nombre, tic_debcre: values.tic_debcre },
                    config
                );
                message.success('Tipo de comprobante actualizado');
            } else {
                const codigo = values.codigo_afip;
                const exists = tiposComprobantes.some(
                    (t) => Number(t.tic_codigo) === Number(codigo)
                );
                if (exists) {
                    message.error('Ya existe un comprobante con ese código AFIP (tic_codigo).');
                    return;
                }
                const url = `${process.env.REACT_APP_API_URL}/tipos-comprobantes`;
                await axios.post(
                    url,
                    {
                        codigo_afip: codigo,
                        tic_nombre: values.tic_nombre,
                        tic_debcre: values.tic_debcre,
                    },
                    config
                );
                message.success('Tipo de comprobante creado');
            }

            setModalVisible(false);
            fetchTiposComprobantes();
        } catch (error) {
            if (error?.errorFields) return;
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                'Error al guardar';
            message.error(msg);
        }
    };

    const columns = [
        {
            title: 'Código AFIP',
            dataIndex: 'tic_codigo',
            key: 'tic_codigo',
            width: 120,
            render: (c) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{c}</span>,
        },
        {
            title: 'Nombre',
            dataIndex: 'tic_nombre',
            key: 'tic_nombre',
        },
        {
            title: 'Débito / crédito',
            dataIndex: 'tic_debcre',
            key: 'tic_debcre',
            width: 130,
            render: (v) => debCreLabel(v),
        },
        {
            title: 'Acciones',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Button type="primary" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
            ),
        },
    ];

    return (
        <div style={{ padding: 16 }}>
            <h1>Tipos de comprobantes</h1>
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                style={{ marginBottom: 16 }}
            >
                Agregar
            </Button>
            {loading ? (
                <div style={{ textAlign: 'center', margin: '20px 0' }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Table
                    dataSource={tiposComprobantes}
                    columns={columns}
                    rowKey="tic_id"
                    pagination={false}
                />
            )}

            <Modal
                title={editing ? 'Editar tipo de comprobante' : 'Nuevo tipo de comprobante'}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                destroyOnClose
                okText="Guardar"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="codigo_afip"
                        label="Código AFIP (tabla ARCA/AFIP → tic_codigo)"
                        rules={[{ required: !editing, message: 'Ingrese el código' }]}
                    >
                        <InputNumber
                            min={1}
                            max={99999}
                            style={{ width: '100%' }}
                            disabled={!!editing}
                            placeholder="Número según tabla AFIP"
                        />
                    </Form.Item>
                    <Form.Item
                        name="tic_nombre"
                        label="Nombre"
                        rules={[{ required: true, message: 'Ingrese el nombre' }]}
                    >
                        <Input placeholder="Ej.: Factura A" />
                    </Form.Item>
                    <Form.Item
                        name="tic_debcre"
                        label="Débito / crédito (asientos / PDF)"
                        rules={[{ required: true, message: 'Seleccione' }]}
                    >
                        <Select placeholder="Seleccione">
                            <Option value={1}>Débito (1)</Option>
                            <Option value={2}>Crédito (2)</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CTiposComprobantesAdministrador;
